import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import RallyCard from '../components/RallyCard';
import { Input } from '../components/ui/input';
import { Bell } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Rallies = () => {
  const [rallies, setRallies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyRallyId, setNotifyRallyId] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const fetchRallies = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/rallies`);
        setRallies(response.data);
      } catch (error) {
        toast.error('Failed to load rallies');
      } finally {
        setLoading(false);
      }
    };
    fetchRallies();
  }, []);

  useEffect(() => {
    const notifyParam = searchParams.get('notify');
    if (notifyParam) {
      setNotifyRallyId(notifyParam);
      setShowNotifyModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (location.hash === '#signup') {
      setTimeout(() => {
        document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location]);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/notify-emails`, {
        email: notifyEmail,
        rally_id: notifyRallyId
      });
      toast.success("You're on the notification list!");
      setShowNotifyModal(false);
      setNotifyEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  const upcomingRallies = rallies.filter((r) => r.status === 'open' || r.status === 'upcoming');
  const completedRallies = rallies.filter((r) => r.status === 'completed');
  const comingSoonRallies = rallies.filter((r) => r.status === 'coming_soon');

  return (
    <div data-testid="rallies-page" className="bg-[#0A0A0A] min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-7xl text-[#F2EDE6] mb-4"
          >
            PICK YOUR RALLY.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[#A19D98] text-lg"
          >
            Every route is different. Every experience is unforgettable.
          </motion.p>
        </div>
      </section>

      {/* Rally Cards */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {upcomingRallies.length > 0 && (
            <div className="mb-16">
              <h2 className="font-['Oswald'] text-2xl text-[#E8651A] mb-8 tracking-wider">
                OPEN FOR REGISTRATION
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {upcomingRallies.map((rally, i) => (
                  <RallyCard key={rally.id} rally={rally} index={i} />
                ))}
              </div>
            </div>
          )}

          {comingSoonRallies.length > 0 && (
            <div className="mb-16">
              <h2 className="font-['Oswald'] text-2xl text-[#A19D98] mb-8 tracking-wider">
                COMING SOON
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {comingSoonRallies.map((rally, i) => (
                  <RallyCard key={rally.id} rally={rally} index={i} />
                ))}
              </div>
            </div>
          )}

          {completedRallies.length > 0 && (
            <div>
              <h2 className="font-['Oswald'] text-2xl text-[#A19D98] mb-8 tracking-wider">
                PAST RALLIES
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {completedRallies.map((rally, i) => (
                  <RallyCard key={rally.id} rally={rally} index={i} />
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center py-16">
              <p className="text-[#A19D98]">Loading rallies...</p>
            </div>
          )}
        </div>
      </section>

      {/* Sign Up CTA */}
      <section id="signup" data-testid="signup-section" className="py-24 px-6 bg-[#141414]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] mb-4">
              READY TO ROLL?
            </h2>
            <p className="text-[#A19D98] mb-10">
              Pick a rally above and hit <span className="text-[#E8651A]">Register for this Rally</span>.
              Whether you're a first-timer or a returning rally member, you'll be on the road in two minutes.
            </p>

            {upcomingRallies.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl mx-auto">
                {upcomingRallies.slice(0, 2).map((r) => (
                  <Link
                    key={r.id}
                    to={`/rallies/${r.id}/register`}
                    data-testid={`signup-cta-${r.id}`}
                    className="bg-[#0A0A0A] border border-[#2A2A2A] hover:border-[#E8651A] p-5 text-left transition-colors"
                  >
                    <p className="text-[#E8651A] font-['Oswald'] tracking-wider text-xs mb-1">REGISTER FOR</p>
                    <p className="text-[#F2EDE6] font-['Oswald'] tracking-wider text-lg">
                      {r.name.toUpperCase()}
                    </p>
                    <p className="text-[#A19D98] text-xs mt-2">{r.dates}</p>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#141414] border border-[#2A2A2A] p-8 max-w-md w-full"
          >
            <Bell className="w-12 h-12 text-[#E8651A] mx-auto mb-4" />
            <h3 className="font-['Oswald'] text-2xl text-[#F2EDE6] text-center mb-2">
              GET NOTIFIED
            </h3>
            <p className="text-[#A19D98] text-center mb-6">
              Enter your email and we'll notify you when registration opens.
            </p>
            <form onSubmit={handleNotifySubmit}>
              <Input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="notify-email-input"
                className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6] mb-4"
              />
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="notify-submit"
                  className="btn-primary flex-1"
                >
                  Notify Me
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Rallies;
